import { useEffect } from 'react';
import { Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/pages/home/components/Footer';

const SITE_URL = 'https://oopbuyspread.mom';
const LOGO_URL = 'https://static.readdy.ai/image/2df560051ef0cafd8dabf8a049058ba4/5133451b25e408e4a25e4df97e05add8.jpeg';
const ARTICLE_IMAGE = 'https://readdy.ai/api/search-image?query=Clean%20minimal%20quality%20control%20inspection%20workspace%20with%20magnifying%20glass%20fabric%20swatches%20and%20checklist%20on%20soft%20cream%20desk%20surface%20natural%20daylight%20scandinavian%20editorial%20photography%20warm%20neutral%20tones%20organized%20professional%20composition&width=1600&height=600&seq=article-hero-qc-v2&orientation=landscape';

const articleSchema = {
  '@context': 'https://schema.org',
  '@type': 'Article',
  headline: 'Oopbuy Spreadsheet QC Guide: Qualitätskontrolle für deine Bestellungen',
  description: 'Der Oopbuy Spreadsheet QC Guide erklärt den mehrstufigen Qualitätskontroll-Prozess – von Wareneingang bis Versandfreigabe.',
  image: ARTICLE_IMAGE,
  datePublished: '2026-07-15',
  dateModified: '2026-07-15',
  author: {
    '@type': 'Organization',
    name: 'Oopbuy Spreadsheet',
    url: SITE_URL,
  },
  publisher: {
    '@type': 'Organization',
    name: 'Oopbuy Spreadsheet',
    logo: {
      '@type': 'ImageObject',
      url: LOGO_URL,
    },
  },
  mainEntityOfPage: {
    '@type': 'WebPage',
    '@id': `${SITE_URL}/artikel/oopbuy-spreadsheet-qc-guide`,
  },
  inLanguage: 'de',
};

export default function QCGuidePage() {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <main>
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(articleSchema) }} />
      <Navbar />

      <article className="pt-[72px]">
        <div className="px-4 md:px-8 lg:px-20 py-6">
          <nav className="flex items-center gap-2 text-sm text-foreground-500">
            <Link to="/" className="hover:text-foreground-900 transition-colors">Home</Link>
            <i className="ri-arrow-right-s-line text-xs"></i>
            <span className="text-foreground-900">Oopbuy Spreadsheet QC Guide</span>
          </nav>
        </div>

        <div className="w-full h-[300px] md:h-[420px] overflow-hidden">
          <img
            src="https://readdy.ai/api/search-image?query=Clean%20minimal%20quality%20control%20inspection%20workspace%20with%20magnifying%20glass%20fabric%20swatches%20and%20checklist%20on%20soft%20cream%20desk%20surface%20natural%20daylight%20scandinavian%20editorial%20photography%20warm%20neutral%20tones%20organized%20professional%20composition&width=1600&height=600&seq=article-hero-qc-v2&orientation=landscape"
            alt="Oopbuy Spreadsheet QC Guide"
            className="w-full h-full object-cover object-top"
          />
        </div>

        <div className="max-w-[780px] mx-auto px-4 md:px-8 py-12 md:py-16">
          <h1 className="text-[32px] md:text-[42px] font-heading font-bold text-foreground-950 leading-tight mb-8 text-center">
            Oopbuy Spreadsheet QC Guide: Qualitätskontrolle für deine Bestellungen
          </h1>

          <p className="text-lg leading-[1.8] text-foreground-700 mb-8">
            Qualität ist kein Zufall – besonders nicht beim Online-Shopping. <strong>Oopbuy Spreadsheet</strong> bietet einen integrierten QC-Prozess (Quality Control), der sicherstellt, dass jede Bestellung den höchsten Standards entspricht. In diesem <strong>Oopbuy Spreadsheet QC Guide</strong> erklären wir dir Schritt für Schritt, wie die Qualitätskontrolle funktioniert und wie du sie optimal nutzt.
          </p>

          <p className="text-lg leading-[1.8] text-foreground-700 mb-8">
            Besuche auch unsere <Link to="/" className="text-primary-600 hover:text-primary-700 underline">Oopbuy Spreadsheet Startseite</Link> für weitere Informationen zu allen Funktionen.
          </p>

          <h2 className="text-[26px] md:text-[30px] font-heading font-bold text-foreground-950 mt-12 mb-6">
            Was ist der QC-Prozess bei Oopbuy Spreadsheet?
          </h2>
          <p className="text-lg leading-[1.8] text-foreground-700 mb-6">
            Der QC-Prozess in <strong>Oopbuy Spreadsheet</strong> ist ein mehrstufiges Prüfverfahren, das automatisch durchgeführt wird, sobald deine Bestellung im System erfasst ist. Jede Bestellung durchläuft mehrere Qualitätschecks, bevor sie an dich versendet wird. Mit <strong>Oopbuy Spreadsheet</strong> hast du volle Transparenz über jeden Schritt.
          </p>

          <div className="space-y-6 mb-8">
            <div className="flex gap-4">
              <div className="w-12 h-12 rounded-full bg-primary-500 text-background-50 flex items-center justify-center flex-shrink-0 font-bold text-base">1</div>
              <div className="bg-secondary-50 rounded-xl p-5 flex-1">
                <h3 className="text-lg font-semibold text-foreground-900 mb-1">Wareneingangsprüfung</h3>
                <p className="text-foreground-600 leading-relaxed">Jedes Produkt wird bei Ankunft im <strong>Oopbuy Spreadsheet</strong> Lager auf äußere Schäden geprüft. Die Ergebnisse werden direkt in dein Spreadsheet eingetragen.</p>
              </div>
            </div>
            <div className="flex gap-4">
              <div className="w-12 h-12 rounded-full bg-primary-500 text-background-50 flex items-center justify-center flex-shrink-0 font-bold text-base">2</div>
              <div className="bg-secondary-50 rounded-xl p-5 flex-1">
                <h3 className="text-lg font-semibold text-foreground-900 mb-1">Detailprüfung</h3>
                <p className="text-foreground-600 leading-relaxed">Größe, Farbe, Material und Verarbeitung werden genau mit deiner Bestellung in <strong>Oopbuy Spreadsheet</strong> abgeglichen. Abweichungen werden sofort markiert.</p>
              </div>
            </div>
            <div className="flex gap-4">
              <div className="w-12 h-12 rounded-full bg-primary-500 text-background-50 flex items-center justify-center flex-shrink-0 font-bold text-base">3</div>
              <div className="bg-secondary-50 rounded-xl p-5 flex-1">
                <h3 className="text-lg font-semibold text-foreground-900 mb-1">Foto-Dokumentation</h3>
                <p className="text-foreground-600 leading-relaxed">In <strong>Oopbuy Spreadsheet</strong> erhältst du Fotos deiner geprüften Produkte. So siehst du genau, was du bekommst – noch vor dem Versand.</p>
              </div>
            </div>
            <div className="flex gap-4">
              <div className="w-12 h-12 rounded-full bg-primary-500 text-background-50 flex items-center justify-center flex-shrink-0 font-bold text-base">4</div>
              <div className="bg-secondary-50 rounded-xl p-5 flex-1">
                <h3 className="text-lg font-semibold text-foreground-900 mb-1">Freigabe & Versand</h3>
                <p className="text-foreground-600 leading-relaxed">Erst nach erfolgreicher Prüfung wird die Bestellung in <strong>Oopbuy Spreadsheet</strong> für den Versand freigegeben. Du erhältst eine automatische Benachrichtigung.</p>
              </div>
            </div>
          </div>

          <h2 className="text-[26px] md:text-[30px] font-heading font-bold text-foreground-950 mt-12 mb-6">
            QC-Checkliste für deine Bestellungen
          </h2>

          <div className="overflow-x-auto mb-8">
            <table className="w-full text-sm border-collapse">
              <thead>
                <tr className="bg-secondary-50">
                  <th className="text-left p-4 font-semibold text-foreground-900 rounded-tl-lg">Prüfkriterium</th>
                  <th className="text-left p-4 font-semibold text-foreground-900">Beschreibung</th>
                  <th className="text-left p-4 font-semibold text-foreground-900 rounded-tr-lg">Status in Oopbuy Spreadsheet</th>
                </tr>
              </thead>
              <tbody>
                <tr className="border-b border-foreground-200/50">
                  <td className="p-4 text-foreground-900 font-medium">Produktzustand</td>
                  <td className="p-4 text-foreground-700">Prüfung auf Beschädigungen</td>
                  <td className="p-4"><span className="px-2 py-1 rounded-full bg-primary-100 text-primary-700 text-xs font-medium">Automatisch</span></td>
                </tr>
                <tr className="border-b border-foreground-200/50">
                  <td className="p-4 text-foreground-900 font-medium">Größencheck</td>
                  <td className="p-4 text-foreground-700">Abgleich mit Bestelldaten</td>
                  <td className="p-4"><span className="px-2 py-1 rounded-full bg-primary-100 text-primary-700 text-xs font-medium">Automatisch</span></td>
                </tr>
                <tr className="border-b border-foreground-200/50">
                  <td className="p-4 text-foreground-900 font-medium">Farbprüfung</td>
                  <td className="p-4 text-foreground-700">Vergleich mit Produktfotos</td>
                  <td className="p-4"><span className="px-2 py-1 rounded-full bg-primary-100 text-primary-700 text-xs font-medium">Automatisch</span></td>
                </tr>
                <tr>
                  <td className="p-4 text-foreground-900 font-medium rounded-bl-lg">Verpackung</td>
                  <td className="p-4 text-foreground-700">Sichere Verpackung für Versand</td>
                  <td className="p-4 rounded-br-lg"><span className="px-2 py-1 rounded-full bg-primary-100 text-primary-700 text-xs font-medium">Automatisch</span></td>
                </tr>
              </tbody>
            </table>
          </div>

          <h2 className="text-[26px] md:text-[30px] font-heading font-bold text-foreground-950 mt-12 mb-6">
            Vorteile der Oopbuy Spreadsheet QC
          </h2>
          <ul className="space-y-4 mb-8">
            <li className="flex items-start gap-3">
              <i className="ri-checkbox-circle-fill text-primary-500 text-xl mt-0.5 flex-shrink-0"></i>
              <div>
                <strong className="text-foreground-900">Weniger Retouren:</strong>
                <span className="text-foreground-700"> Durch die QC-Prüfung in <strong>Oopbuy Spreadsheet</strong> reduzierst du Retouren um bis zu 80%.</span>
              </div>
            </li>
            <li className="flex items-start gap-3">
              <i className="ri-checkbox-circle-fill text-primary-500 text-xl mt-0.5 flex-shrink-0"></i>
              <div>
                <strong className="text-foreground-900">Volle Transparenz:</strong>
                <span className="text-foreground-700"> Du siehst in <strong>Oopbuy Spreadsheet</strong> jeden Prüfschritt und jedes QC-Foto.</span>
              </div>
            </li>
            <li className="flex items-start gap-3">
              <i className="ri-checkbox-circle-fill text-primary-500 text-xl mt-0.5 flex-shrink-0"></i>
              <div>
                <strong className="text-foreground-900">Kostenlose Nachbesserung:</strong>
                <span className="text-foreground-700"> Bei QC-Mängeln wird das Produkt in <strong>Oopbuy Spreadsheet</strong> kostenlos nachgebessert.</span>
              </div>
            </li>
          </ul>

          <p className="text-lg leading-[1.8] text-foreground-700 mb-6">
            Mehr Tipps findest du auch im <Link to="/artikel/oopbuy-spreadsheet-shipping-guide" className="text-primary-600 hover:text-primary-700 underline">Oopbuy Spreadsheet Versand Guide</Link> – dort erfährst du, wie QC und Versand Hand in Hand arbeiten.
          </p>

          <div className="bg-primary-50 rounded-2xl p-8 md:p-10 text-center my-12">
            <h2 className="text-[24px] md:text-[28px] font-heading font-bold text-foreground-950 mb-3">
              Sicheres Einkaufen mit Oopbuy Spreadsheet QC
            </h2>
            <p className="text-foreground-600 mb-6 max-w-md mx-auto">
              Qualitätskontrolle, die funktioniert – integriert in dein <strong>Oopbuy Spreadsheet</strong>.
            </p>
            <div className="flex flex-col sm:flex-row gap-3 justify-center">
              <a
                href="https://kaspreadsheet.com"
                target="_blank"
                rel="noopener nofollow"
                className="inline-flex items-center justify-center gap-2 h-12 px-6 rounded-full bg-primary-500 text-background-50 text-sm font-semibold hover:bg-primary-600 transition-colors whitespace-nowrap cursor-pointer"
              >
                Oopbuy Spreadsheet
                <i className="ri-arrow-right-up-line text-sm"></i>
              </a>
              <a
                href="https://kaspreadsheet.com"
                target="_blank"
                rel="noopener nofollow"
                className="inline-flex items-center justify-center gap-2 h-12 px-6 rounded-full border-2 border-primary-300 text-primary-700 text-sm font-semibold hover:bg-primary-100 transition-colors whitespace-nowrap cursor-pointer"
              >
                Buy It Now
                <i className="ri-shopping-bag-line text-sm"></i>
              </a>
            </div>
          </div>

          <div className="border-t border-foreground-200/50 pt-8 mt-8">
            <p className="text-sm text-foreground-500">
              <i className="ri-arrow-left-line mr-1"></i>
              Zurück zur <Link to="/" className="text-primary-600 hover:text-primary-700 underline font-medium">Oopbuy Spreadsheet Startseite</Link> – entdecke alle Guides und Kategorien.
            </p>
          </div>
        </div>
      </article>

      <Footer />
    </main>
  );
}